import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  BookmarkIcon,
  CheckIcon,
  DownloadIcon,
  EyeIcon,
  FileIcon,
  FilePenIcon,
  FileTextIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PinIcon,
  Redo2Icon,
  ReplyIcon,
  SaveIcon,
  TargetIcon,
  ThumbsUpIcon,
  Trash2Icon,
  TriangleAlertIcon,
  Undo2Icon,
  UploadIcon,
  UserPlusIcon,
  UserXIcon,
  ViewIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { LabelChooser } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { css, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { EntityToolbar } from "components/lib/EntityHeader";
import { IdeaFollowButton } from "components/lib/FollowButton";
import IdeaPdfExport from "components/lib/IdeaPdfExport";
import {
  IdeaChallengeHelp,
  IdeaLikeHandler,
  SubmitIdeaButton,
} from "components/lib/Ideas";
import type { IdeaPreviewOwnerChallenge } from "components/lib/Ideas/IdeaPreview/types";
import { useTrayStatus } from "components/lib/Notifications";

import ImportWordModal from "./ImportWordModal";
import { useIdea } from "./hooks/useIdea";
import type { IdeaCollaboratorUser } from "./types";

const optionsMenuClass = css`
  width: 300px;
  min-width: 0 !important;
`;

const ToolbarDivider = styled.span`
  width: 1px;
  height: 24px;
  background: #e4e7ec;
  margin: 0 4px;
  flex: none;
`;

type IdeaStatusBarProps = {
  updateIdea: (data: unknown, path: string[], replace?: boolean) => void;
  isSaving: boolean;
  unsaved: boolean;
  isEditing: boolean;
  setEditing: (isEditing: boolean) => void;
  savedAt?: string | Date | "";
  onSubmit: (isSubmitted: boolean) => void;
  otherUsers: Record<string, IdeaCollaboratorUser>;
  websocketConnected: boolean;
  deleteIdea: () => void;
  leaveIdea: () => void;
  stickyTop?: number;
  /** When set (idea preview), show “Open full idea” beside the ⋯ menu. */
  previewFullIdeaHref?: string;
  /** Mobile idea preview: close control left of the like button. */
  onClosePreview?: () => void;
  undoChanges?: () => void;
  redoChanges?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  reloadIdea?: () => void;
};

const IdeaStatusBar = ({
  updateIdea,
  isSaving,
  unsaved,
  isEditing,
  setEditing,
  savedAt,
  onSubmit,
  otherUsers = {},
  websocketConnected,
  deleteIdea,
  leaveIdea,
  stickyTop = 50,
  previewFullIdeaHref,
  onClosePreview,
  undoChanges,
  redoChanges,
  canUndo = false,
  canRedo = false,
  reloadIdea,
}: IdeaStatusBarProps) => {
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [labelChooserOpen, setLabelChooserOpen] = useState(false);
  const [isUnsubmitting, setIsUnsubmitting] = useState(false);
  const [importWordModalOpen, setImportWordModalOpen] = useState(false);
  const [_, setTrayStatus] = useTrayStatus();

  const otherUsersValues = useMemo(
    () => Object.values(otherUsers) as IdeaCollaboratorUser[],
    [otherUsers],
  );

  const canManageChallenge = util.canManageChallenge(
    user!,
    idea?.ownerChallenge as IdeaPreviewOwnerChallenge | undefined,
  );
  const canEditIdea = util.canEditIdea(user, idea);
  const canEditIdeas =
    util.hasPermission(user, "challenge.editIdeas", idea?.challenge) ||
    util.hasPermission(
      user,
      "org.manageChallenges",
      idea?.ownerChallenge?.organisation,
    );
  const canUpdateIdeaStatus = util.hasPermission(
    user,
    "challenge.updateIdeaStatus",
    idea?.challenge,
  );
  const canUnsubmit = canEditIdea || canManageChallenge || canUpdateIdeaStatus;
  const canPin =
    canManageChallenge ||
    util.hasPermission(user!, "challenge.pinIdeas", idea?.challenge);
  const canDelete =
    canEditIdea ||
    util.hasPermission(
      user!,
      "org.managePermissions",
      user!.ownerOrganisation?._id,
    );
  const canEditLabels = util.hasPermission(
    user!,
    "challenge.editIdeaLabels",
    idea?.challenge,
  );
  const canCreateChallengeFromIdea =
    util.hasPermission(
      user!,
      "challenge.moveIdeaToChallenge",
      idea?.challenge,
    ) && Boolean(idea?.challengeCreation);
  const canCollaborate = Boolean(user && idea && idea.user !== user._id);
  const canDownloadCover =
    !idea?.ownerChallenge?.preventIdeaCoverImages && Boolean(idea?.coverImage);

  const canExportWhileSubmitted = useMemo(
    () =>
      util.hasPermission(
        user,
        "org.manageChallenges",
        idea?.ownerChallenge?.organisation,
      ) || util.hasPermission(user, "challenge.editIdeas", idea?.challenge),
    [user, idea],
  );

  const canImportFromWord = useMemo(
    () =>
      util.canEditIdea(user, idea) &&
      user?.ownerOrganisation?.superadminTags?.innovationType ===
        "externalInnovation",
    [user, idea],
  );

  const showEditToolbarActions =
    isEditing && Boolean(undoChanges) && Boolean(redoChanges);

  const downloadIdeaDocxReport = useCallback(() => {
    if (!user?._id || !idea?._id) {
      return;
    }
    const ideaName =
      idea?.name || t("common:capitalise", { key: "generic.idea" });
    api.users.createDownload(
      user._id,
      {
        downloadType: "ideaDocxExport",
        displayName: `${ideaName} export.docx`,
        idea: idea._id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            ...prev,
            downloads: [download, ...(prev?.downloads || [])],
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  }, [idea, user, setTrayStatus, t]);

  const showDraftActions =
    !previewFullIdeaHref &&
    !idea?.isSubmitted &&
    (canEditIdea || canManageChallenge || canUpdateIdeaStatus) &&
    !idea?.children;

  const showMobileDraftEditToggle =
    theme.sizes.isMobile && showDraftActions && canEditIdea;

  const showOptionsMenu =
    Boolean(idea) &&
    (showEditToolbarActions ||
      showMobileDraftEditToggle ||
      canPin ||
      canCollaborate ||
      canCreateChallengeFromIdea ||
      canDownloadCover ||
      canEditLabels ||
      canDelete ||
      (idea?.isSubmitted && (canEditIdeas || canUnsubmit)));

  const togglePreviewEdit = () => {
    const nextEditing = !isEditing;
    setEditing(nextEditing);
    api.journey.record(
      "ideas",
      idea?._id,
      nextEditing ? "resumedEditingIdea" : "previewedIdea",
    );
  };

  const ideaId = idea?._id;
  const userId = user?._id;
  const ideaChallenge = idea?.challenge;
  const canVote =
    Boolean(idea?.ownerChallenge) &&
    idea?.ownerChallenge?.votingVisibility === "users";

  const onFollow = useCallback(
    (context: string, id: string) => {
      dispatch(actions.user.onFollow(context, id));
    },
    [dispatch],
  );

  const voteForIdea = useCallback(
    (addVote: boolean) => {
      if (!ideaId) return;
      api.ideas.vote(
        ideaId,
        addVote,
        ({ addedVotesFor, removedVotesFor }) => {
          const newIdea = {
            votedFor: idea?.votedFor,
            voteCount: idea?.voteCount || 0,
          };

          if (!newIdea.voteCount) newIdea.voteCount = 0;
          if (addedVotesFor.indexOf(ideaId) > -1) {
            onFollow("challenge", ideaChallenge);
            newIdea.votedFor = true;
            newIdea.voteCount += 1;
          }
          if (removedVotesFor.indexOf(ideaId) > -1) {
            newIdea.votedFor = false;
            newIdea.voteCount -= 1;
          }
          updateIdea(newIdea.voteCount, ["voteCount"], true);
          updateIdea(newIdea.votedFor, ["votedFor"], true);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [
      ideaId,
      updateIdea,
      ideaChallenge,
      idea?.votedFor,
      idea?.voteCount,
      onFollow,
    ],
  );

  const joinIdea = useCallback(() => {
    if (!ideaId || !userId) return;
    api.ideas.addCollaborator(
      ideaId,
      userId,
      ({ authors, collaborators }) => {
        updateIdea(authors, ["authors"], true);
        updateIdea(collaborators, ["collaborators"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, userId, updateIdea]);

  const pinIdea = useCallback(() => {
    if (!ideaId) return;
    api.ideas.updateStatus(
      ideaId,
      { isPinned: !idea?.isPinned },
      (ideaStatus) => {
        updateIdea(ideaStatus.isPinned, ["isPinned"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, idea?.isPinned, updateIdea]);

  const labelIdea = useCallback(
    (label: string, isApplied: boolean) => {
      if (!ideaId) return;
      let labels = idea?.labels || [];
      if (labels.includes(label)) {
        if (!isApplied) labels = labels.filter((l) => l !== label);
      } else if (isApplied) {
        labels = [...labels, label];
      }
      api.ideas.updateStatus(
        ideaId,
        { labels },
        () => updateIdea(labels, ["labels"], true),
        (err) => toast.error(err.message),
      );
    },
    [ideaId, idea?.labels, updateIdea],
  );

  const createChallengeFromIdea = useCallback(() => {
    if (!ideaId) return;
    api.challenges.createFromIdea(
      { idea: ideaId },
      (data) => {
        navigate(`/challenges/${data.challenge._id}`);
        toast.success("Challenge created");
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, navigate]);

  const unsubmitIdea = useCallback(() => {
    if (!ideaId) return;
    util
      .confirm(
        t("ideas.submit.confirm.withdraw.title"),
        t("ideas.submit.confirm.withdraw.info"),
      )
      .then(() => {
        setIsUnsubmitting(true);
        api.ideas.updateStatus(
          ideaId,
          { isSubmitted: false },
          (newIdea) => {
            util.onUpdateIdea(newIdea, newIdea);
            toast.success(t("ideas.submit.confirm.unsubmitSuccess"));
            onSubmit(false);
            setIsUnsubmitting(false);
            api.invalidateQueries(api.challenges.getIdeas, [newIdea.challenge]);
          },
          (err) => {
            if (err.status === 400) {
              updateIdea("closed", ["ownerChallenge", "stage"], true);
            }
            toast.error(err.message);
            setIsUnsubmitting(false);
          },
        );
      })
      .catch(() => {});
  }, [ideaId, t, onSubmit, updateIdea]);

  // Dividers only separate groups that are actually shown, so none dangle at the edges
  const hasItemsBeforeEditActions =
    (isEditing && !websocketConnected && !theme.sizes.isMobile) ||
    (!theme.sizes.isMobile && otherUsersValues.length > 0) ||
    !!(previewFullIdeaHref && !loading && idea);
  const hasItemsBeforeOptionsMenu =
    hasItemsBeforeEditActions ||
    showEditToolbarActions ||
    loading ||
    !!showDraftActions;

  return (
    <EntityToolbar stickyTop={stickyTop} style={{ zIndex: 300 }}>
      <ImportWordModal
        open={importWordModalOpen}
        onClose={() => setImportWordModalOpen(false)}
        ideaId={idea?._id}
        onImported={() => reloadIdea?.()}
      />
      <LabelChooser
        isOpen={labelChooserOpen}
        onClose={() => setLabelChooserOpen(false)}
        onComplete={(em) => labelIdea(em, true)}
        existing={idea?.labels}
        onRemoveExisting={(em) => labelIdea(em, false)}
      />

      {idea ? (
        <Modal open={helpModalOpen} onClose={() => setHelpModalOpen(false)}>
          <ModalHeader>
            {t("common:capitalise", { key: "generic.challenge" })} overview
          </ModalHeader>
          <ModalContent scrolling>
            <IdeaChallengeHelp challengeId={idea?.challenge} />
          </ModalContent>
          <ModalActions>
            <Button onClick={() => setHelpModalOpen(false)}>
              {t("generic.close")}
            </Button>
          </ModalActions>
        </Modal>
      ) : null}

      <div>
        {loading ? (
          <Placeholder style={{ width: 150 }}>
            <PlaceholderLine style={{ height: 25 }} />
          </Placeholder>
        ) : !theme.sizes.isMobile ? (
          idea?.isSubmitted ? (
            <Popup
              trigger={<Label color="green">{t("generic.submitted")}</Label>}
            >{`Submitted ${formatDate(idea?.submittedAt)}`}</Popup>
          ) : (
            <Label color="yellow">
              <Icon icon={FileIcon} /> {t("generic.draft")}
            </Label>
          )
        ) : null}
        {idea?.ownerChallenge && !theme.sizes.isMobile ? (
          <ButtonLink
            size="small"
            variant="outline"
            to={`/challenges/${idea?.challenge}`}
            label={
              <Popup
                trigger={
                  <Label size="small" onClick={() => setHelpModalOpen(true)}>
                    <Icon icon={EyeIcon} />
                  </Label>
                }
              >
                {t("challenges.viewDetails")}
              </Popup>
            }
          >
            {idea.ownerChallenge.name}
          </ButtonLink>
        ) : null}
        {!theme.sizes.isMobile && !loading ? (
          <IdeaFollowButton size="small" />
        ) : null}
        {onClosePreview && !theme.sizes.isComputer ? (
          <Button
            size="small"
            variant="outline"
            onClick={onClosePreview}
          >{`Close preview`}</Button>
        ) : null}
        {canVote && idea?.ownerChallenge ? (
          <IdeaLikeHandler
            idea={idea}
            challenge={idea.ownerChallenge}
            onLikeIdea={(_id, addVote) => voteForIdea(addVote)}
            trigger={
              <Button
                aria-label="Like idea"
                size="small"
                variant="soft"
                label={
                  idea.ownerChallenge.voteCountVisibility === "users" ? (
                    <Label basic pointing="left">
                      {idea?.voteCount || 0}
                    </Label>
                  ) : null
                }
                color={idea?.votedFor ? "primary" : undefined}
              >
                <Icon icon={ThumbsUpIcon} />
              </Button>
            }
          />
        ) : null}
      </div>

      <div>
        {isEditing && !websocketConnected && !theme.sizes.isMobile ? (
          <Popup
            trigger={
              <Icon color="yellow" size="large" icon={TriangleAlertIcon} />
            }
          >
            <H4>
              Connection issues
              <Subheader>
                {`Collaboration features are disabled. Your ${t("generic.ideas")} will still be saved automatically.`}
              </Subheader>
            </H4>
          </Popup>
        ) : null}

        {!theme.sizes.isMobile && otherUsersValues.length ? (
          <Popup
            trigger={
              <div>
                {otherUsersValues.map((u, index) => (
                  <ImageWithFallback
                    key={u._id}
                    avatar
                    src={util.avatarUrl(u)}
                    fallbackSrc={util.avatarUrl()}
                    style={{ marginLeft: index === 0 ? 0 : -8, minWidth: 25 }}
                  />
                ))}
              </div>
            }
          >
            <H4>
              Collaborators online
              <Subheader>
                {otherUsersValues.map((u) => u.profile?.fullName).join(", ")}
              </Subheader>
            </H4>
          </Popup>
        ) : null}

        {previewFullIdeaHref && !loading && idea ? (
          <ButtonLink size="small" variant="outline" to={previewFullIdeaHref}>
            <Icon icon={FileIcon} />
            {`Open full ${t("generic.idea")}`}
          </ButtonLink>
        ) : null}

        {showEditToolbarActions ? (
          <>
            {hasItemsBeforeEditActions ? <ToolbarDivider /> : null}
            <Popup
              on="hover"
              trigger={
                <Button
                  size="small"
                  onClick={undoChanges}
                  disabled={!canUndo}
                  variant="outline"
                >
                  <Icon icon={Undo2Icon} />
                </Button>
              }
            >
              {t("ideas.undo")}
            </Popup>
            <Popup
              on="hover"
              trigger={
                <Button
                  size="small"
                  onClick={redoChanges}
                  disabled={!canRedo}
                  variant="outline"
                >
                  <Icon icon={Redo2Icon} />
                </Button>
              }
            >
              {t("ideas.redo")}
            </Popup>
            {!theme.sizes.isMobile && canEditIdea && !idea?.isSubmitted ? (
              <Button
                size="small"
                variant="outline"
                onClick={() => {
                  toast(t("ideas.autosave.warning"));
                }}
              >
                <Icon icon={SaveIcon} />
                {savedAt
                  ? t("generic.savedAt", { time: formatDate(savedAt, "HH:mm") })
                  : t("generic.save")}
              </Button>
            ) : null}
          </>
        ) : null}

        {loading ? (
          <Placeholder style={{ width: 150 }}>
            <PlaceholderLine style={{ height: 25 }} />
          </Placeholder>
        ) : showDraftActions ? (
          <>
            {hasItemsBeforeEditActions || showEditToolbarActions ? (
              <ToolbarDivider />
            ) : null}
            {canEditIdea && !theme.sizes.isMobile ? (
              <Button
                size="small"
                variant="outline"
                onClick={togglePreviewEdit}
              >
                <Icon icon={ViewIcon} />
                {t(isEditing ? "ideas.header.preview" : "ideas.header.edit")}
              </Button>
            ) : null}
            <SubmitIdeaButton
              updateIdea={updateIdea}
              onSubmit={onSubmit}
              isSaving={isSaving}
              unsaved={unsaved}
              isStatusBar={true}
              size="small"
            />
          </>
        ) : null}

        {showOptionsMenu && idea ? (
          <>
            {hasItemsBeforeOptionsMenu ? <ToolbarDivider /> : null}
            <Dropdown
              direction="left"
              trigger={
                <Button
                  size="small"
                  variant="ghost"
                  data-testid="idea-options-dropdown"
                  aria-label="More actions"
                >
                  <Icon icon={MoreHorizontalIcon} />
                </Button>
              }
            >
              <DropdownMenu className={optionsMenuClass}>
                {showMobileDraftEditToggle ? (
                  <>
                    <DropdownItem onClick={togglePreviewEdit}>
                      <Icon icon={isEditing ? ViewIcon : PencilIcon} />
                      {t(
                        isEditing
                          ? "ideas.header.preview"
                          : "ideas.header.edit",
                      )}
                    </DropdownItem>
                    <DropdownDivider />
                  </>
                ) : null}

                {idea.isSubmitted && canEditIdeas ? (
                  <DropdownItem onClick={() => setEditing(!isEditing)}>
                    <Icon icon={isEditing ? CheckIcon : PencilIcon} />
                    {isEditing ? "Done editing" : `Edit ${t("generic.idea")}`}
                  </DropdownItem>
                ) : null}

                {idea.isSubmitted && canUnsubmit ? (
                  <DropdownItem
                    onClick={unsubmitIdea}
                    disabled={isUnsubmitting}
                  >
                    <Icon
                      icon={
                        canEditIdeas ||
                        util.hasPermission(
                          user,
                          "org.manageChallenges",
                          idea?.ownerChallenge?.organisation,
                        )
                          ? ReplyIcon
                          : PencilIcon
                      }
                    />
                    {canEditIdea
                      ? `Unsubmit ${t("generic.idea")}`
                      : t("generic.unsubmit")}
                  </DropdownItem>
                ) : null}

                {idea.isSubmitted && (canEditIdeas || canUnsubmit) ? (
                  <DropdownDivider />
                ) : null}

                <IdeaPdfExport
                  hidePopup
                  ideas={[idea]}
                  challenge={idea.ownerChallenge}
                  trigger={
                    <DropdownItem>
                      <Icon icon={FileTextIcon} />
                      {t("ideas.options.download.pdf")}
                    </DropdownItem>
                  }
                />

                {isEditing ? (
                  <DropdownItem
                    onClick={() => downloadIdeaDocxReport()}
                    disabled={idea.isSubmitted && !canExportWhileSubmitted}
                  >
                    <Icon icon={FilePenIcon} />
                    {t("ideas.download.word")}
                  </DropdownItem>
                ) : null}

                {isEditing && canImportFromWord ? (
                  <DropdownItem
                    onClick={() => setImportWordModalOpen(true)}
                    disabled={idea.isSubmitted}
                  >
                    <Icon icon={UploadIcon} />
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {t("ideas.importWord.menuItem")}
                      <Label size="small" color="violet">
                        {t("generic.beta")}
                      </Label>
                    </span>
                  </DropdownItem>
                ) : null}

                {canPin ? (
                  <DropdownItem onClick={pinIdea}>
                    <Icon icon={PinIcon} />
                    {idea.isPinned
                      ? t("ideas.options.pin.unPin")
                      : t("ideas.options.pin.challenge")}
                  </DropdownItem>
                ) : null}

                {canCollaborate ? (
                  <DropdownItem onClick={canEditIdea ? leaveIdea : joinIdea}>
                    <Icon icon={canEditIdea ? UserXIcon : UserPlusIcon} />
                    {canEditIdea
                      ? `Leave ${t("generic.idea")}`
                      : `Collaborate on this ${t("generic.idea")}`}
                  </DropdownItem>
                ) : null}

                {canCreateChallengeFromIdea ? (
                  <DropdownItem onClick={() => createChallengeFromIdea()}>
                    <Icon icon={TargetIcon} />
                    Create {t("generic.challengeWithArticle")} from this{" "}
                    {t("generic.idea")}
                  </DropdownItem>
                ) : null}

                {canDownloadCover ? (
                  <DropdownItemLink
                    href={idea.coverImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon={DownloadIcon} />
                    {t("ideas.download.coverImage")}
                  </DropdownItemLink>
                ) : null}

                {canEditLabels ? (
                  <DropdownItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLabelChooserOpen(true);
                    }}
                  >
                    <Icon icon={BookmarkIcon} />
                    Apply a label
                  </DropdownItem>
                ) : null}

                {canDelete ? (
                  <>
                    <DropdownDivider />
                    <DropdownItem onClick={() => deleteIdea()}>
                      <Icon icon={Trash2Icon} />
                      {`Delete ${t("generic.idea")}`}
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </Dropdown>
          </>
        ) : null}
      </div>
    </EntityToolbar>
  );
};

export default IdeaStatusBar;
